import { Component } from 'preact';
import { ZONES, aggregateAndSort, stripNameSuffix } from '../DeckEditorSidebar';
import { artGroupDisplayName } from '../../utils/sorcery/artGroups';
import { resolveAvatarRules } from '../../utils/game/avatarRules';
import { isPeasantLegal } from '../../utils/sorcery/deckRules';
import { isFreePlay } from '../../utils/freePlay';
import { playUI, UI } from '../../utils/arena/uiSounds';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, PANEL_BG, ACCENT_GOLD,
  SECTION_HEADER_STYLE,
} from '../../lib/medievalTheme';
import { t } from '../../localization/t';

// Phone-only deck panel for the deck editor: the desktop sidebar's data
// model (zone buckets, aggregated rows) rendered as tappable rem-sized
// rows. No hover previews, no HTML5 drag, no hotkeys. Tap a row for the
// inspector, tap the steppers to add/remove. Collapses to a slim count
// rail so the collection can use the full width; collapsing never
// unmounts the collection, so its scroll position survives.

const ZONE_SHORT = { avatar: 'A', spellbook: 'S', atlas: 'T', collection: 'C' };

const STEP_BTN_STYLE = {
  width: '2rem',
  height: '2rem',
  borderRadius: 'var(--radius-md)',
  background: `${GOLD} 0.07)`,
  border: `1px solid ${GOLD} 0.22)`,
  color: '#f4e4b8',
};

export default class MobileDeckPanel extends Component {
  // selectedKey: row the user tapped. Rows are too narrow for permanent
  // steppers, so a row shows its − / + only while selected.
  state = { collapsed: false, selectedKey: null };

  toggleCollapsed = () => {
    playUI(UI.HOVER, { volume: 0.5 });
    this.setState((s) => ({ collapsed: !s.collapsed }));
  };

  groupByZone() {
    const grouped = { avatar: [], spellbook: [], atlas: [], collection: [] };
    for (const entry of this.props.chosenCards || []) {
      // Trust the per-entry zone when set (Imposter decks park extra
      // avatars outside the avatar slot); type-based fallback only for
      // legacy entries with no zone. Same rules as DeckEditorSidebar.
      let zone;
      if (entry.zone && grouped[entry.zone]) {
        zone = entry.zone;
      } else if (entry.card?.type === 'Avatar' || entry.card?._sorceryCategory === 'Avatar') {
        zone = 'avatar';
      } else if (entry.card?.type === 'Site' || entry.card?._sorceryCategory?.toLowerCase() === 'site') {
        zone = 'atlas';
      } else if (entry.isSideboard) {
        zone = 'collection';
      } else {
        zone = 'spellbook';
      }
      grouped[zone].push(entry);
    }
    return grouped;
  }

  visibleZones() {
    const cards = this.props.chosenCards || [];
    const avatar = cards.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
      || cards.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
      || null;
    const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;
    return rules?.forbidAtlas ? ZONES.filter((z) => z.key !== 'atlas') : ZONES;
  }

  toggleRowSelected = (key) => {
    this.setState((s) => ({ selectedKey: s.selectedKey === key ? null : key }));
  };

  renderRow(row, zoneKey) {
    const { ownedMap, gameMode, invalidEntries, onIncrement, onDecrement, onRowPress } = this.props;
    const owned = ownedMap?.get(row.card.unique_id) || 0;
    const isSandbox = gameMode === 'sandbox';
    const isUnowned = !isSandbox && !isFreePlay() && owned < row.qty;
    const isIllegal = gameMode === 'peasant' && !isPeasantLegal(row.card);
    const isRuleInvalid = !!invalidEntries?.has(row.card.unique_id);
    const nameColor = isRuleInvalid ? '#ff9090' : isIllegal ? '#f0b260' : isUnowned ? '#e89090' : TEXT_BODY;
    const key = `${row.card.unique_id}::${row.artGroup}::${row.foiling}`;
    const selected = this.state.selectedKey === key;
    const stop = (e) => e.stopPropagation();

    return (
      <div
        key={key}
        className="flex items-center gap-1.5 px-1"
        style={{
          height: '2.5rem',
          borderRadius: 'var(--radius-md)',
          background: selected ? `${GOLD} 0.1)` : 'transparent',
          border: `1px solid ${selected ? `${GOLD} 0.35)` : 'transparent'}`,
        }}
        onPointerDown={onRowPress ? (e) => onRowPress(e, row, zoneKey) : undefined}
        onClick={() => this.toggleRowSelected(key)}
      >
        <span
          className="shrink-0 inline-flex items-center justify-center text-xs font-semibold tabular-nums"
          style={{
            minWidth: '1.75rem', height: '1.5rem', borderRadius: 'var(--radius-sm)',
            background: `${GOLD} 0.07)`, border: `1px solid ${GOLD} 0.22)`,
            color: isUnowned ? '#e89090' : `#f4e4b8`,
          }}
          title={isUnowned ? t('Own {owned}, need {needed}', { owned, needed: row.qty }) : undefined}
        >
          {isUnowned ? `${owned}/${row.qty}` : `${row.qty}x`}
        </span>
        <span className="flex-1 min-w-0 text-xs truncate" style={{ color: nameColor }}>
          {stripNameSuffix(artGroupDisplayName(row.card, row.artGroup))}
        </span>
        {selected ? (
          <>
            <button
              type="button"
              aria-label={t('Remove one {card}', { card: row.card.name })}
              className="shrink-0 inline-flex items-center justify-center text-base font-bold active:scale-[0.94]"
              style={STEP_BTN_STYLE}
              onPointerDown={stop}
              onClick={(e) => { stop(e); onDecrement?.(row.card.unique_id, row.artGroup, row.foiling); }}
            >
              &minus;
            </button>
            <button
              type="button"
              aria-label={t('Add one {card}', { card: row.card.name })}
              className="shrink-0 inline-flex items-center justify-center text-base font-bold active:scale-[0.94]"
              style={STEP_BTN_STYLE}
              onPointerDown={stop}
              onClick={(e) => { stop(e); onIncrement?.(row.card.unique_id, row.artGroup, row.foiling); }}
            >
              +
            </button>
          </>
        ) : row.card.cost ? (
          <span
            className="shrink-0 flex items-center justify-center rounded-full text-xs font-bold"
            style={{
              width: '1.25rem', height: '1.25rem',
              background: '#17120a', border: `1px solid ${GOLD} 0.45)`, color: ACCENT_GOLD,
            }}
          >
            {row.card.cost}
          </span>
        ) : null}
      </div>
    );
  }

  renderRail(grouped, visibleZones, totalCount) {
    return (
      <button
        type="button"
        aria-label={t('Expand deck panel')}
        className="flex flex-col items-center gap-2 h-full w-full pt-2"
        style={{ background: 'none', border: 'none' }}
        onClick={this.toggleCollapsed}
      >
        <span className="text-sm" style={{ color: ACCENT_GOLD }}>&#9664;</span>
        <span className="text-[0.625rem] font-bold" style={{ color: TEXT_MUTED }}>{totalCount}</span>
        {visibleZones.map(({ key }) => (
          <span key={key} className="flex flex-col items-center leading-tight">
            <span className="text-[0.625rem] font-bold" style={{ color: `${GOLD} 0.55)` }}>{ZONE_SHORT[key]}</span>
            <span className="text-[0.625rem] tabular-nums" style={{ color: TEXT_BODY }}>{grouped[key].length}</span>
          </span>
        ))}
      </button>
    );
  }

  render() {
    const { chosenCards = [], dragActive, dragOverZone, dragAllowedZones } = this.props;
    // A live drag needs the drop targets on screen, so it overrides the
    // collapsed state without changing it.
    const collapsed = this.state.collapsed && !dragActive;
    const grouped = this.groupByZone();
    const visibleZones = this.visibleZones();
    const totalCount = chosenCards.length;

    return (
      <div
        className="flex flex-col h-full shrink-0 select-none overflow-hidden"
        style={{
          width: collapsed ? '2.75rem' : '13rem',
          transition: 'width 180ms cubic-bezier(0.22, 1, 0.36, 1)',
          background: PANEL_BG,
          boxShadow: [
            '-0.375rem 0 1rem rgba(0,0,0,0.45)',
            'inset 1px 0 0 rgba(0,0,0,0.6)',
            `inset 2px 0 0 ${GOLD} 0.28)`,
          ].join(', '),
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        {collapsed ? this.renderRail(grouped, visibleZones, totalCount) : (
          <>
            <div className="shrink-0 px-2 pt-2 pb-1 flex items-center gap-1.5">
              <button
                type="button"
                aria-label={t('Collapse deck panel')}
                className="shrink-0 inline-flex items-center justify-center active:scale-[0.94]"
                style={{ ...STEP_BTN_STYLE, background: 'none', border: 'none', color: ACCENT_GOLD }}
                onClick={this.toggleCollapsed}
              >
                &#9654;
              </button>
              <span className="text-sm font-bold" style={{ color: TEXT_PRIMARY }}>{t('Deck')}</span>
              <span className="ml-auto text-xs tabular-nums" style={{ color: TEXT_MUTED }}>
                {totalCount === 1 ? t('1 Card') : t('{n} Cards', { n: totalCount })}
              </span>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-1 pb-2 medieval-scrollbar">
              {visibleZones.map(({ key, label }) => {
                const entries = grouped[key];
                const rows = entries.length ? aggregateAndSort(entries, 'name', 'asc') : [];
                const isDropTarget = dragActive && (dragAllowedZones || []).includes(key);
                const isDragOver = dragOverZone === key;
                return (
                  <div
                    key={key}
                    className="mb-1"
                    data-deck-zone={key}
                    style={isDropTarget ? {
                      borderRadius: 'var(--radius-md)',
                      background: isDragOver ? `${GOLD} 0.08)` : 'transparent',
                      outline: `1.5px dashed ${GOLD} ${isDragOver ? '0.6' : '0.3'})`,
                      outlineOffset: '-1px',
                      minHeight: '3.25rem',
                    } : undefined}
                  >
                    <div
                      className="sticky top-0 z-[2] flex items-center gap-1.5 px-2 py-1.5"
                      style={{ background: isDragOver ? '#1e1608' : '#0e0b08', borderRadius: 'var(--radius-sm)' }}
                    >
                      <span className="text-xs font-semibold" style={SECTION_HEADER_STYLE}>{t(label)}</span>
                      <span className="text-[0.625rem] ml-auto" style={{ color: `${GOLD} 0.35)` }}>
                        ({entries.length})
                      </span>
                    </div>
                    {rows.map((row) => this.renderRow(row, key))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }
}
